import React from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Flag, KeyRound, Lock, Plus, UsersRound } from "lucide-react";
import { createGroup, fetchGroups, type GroupListItem, type GroupMemberIdentity } from "../../api/groups";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { useIsPro } from "../../hooks/useIsPro";
import { usePageMeta } from "../../hooks/usePageMeta";
import { parseGroupCode } from "../../lib/pending-group-join";
import {
  CARD_STYLE,
  Dialog,
  ErrorLine,
  GroupImagePicker,
  GroupsPageFrame,
  HERO_CONTROL_STYLE,
  HERO_TEXT,
  INPUT_STYLE,
  LABEL_STYLE,
  LIFT_CSS,
  MiniAvatar,
  daysBetween,
  fmtNumber,
  groupErrorMessage,
  isUpgradeRequired,
  londonToday,
  memberName,
} from "./group-ui";

// /groups (spec 32 / ATL-151): your groups as photo-led cards, join with a
// code and create (Pro) in dialogs, and an explainer when you're in none.

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

const firstName = (m: GroupMemberIdentity) => memberName(m).split(/\s+/)[0] ?? memberName(m);

/** Cover art for a group card: its image, a mosaic of collection covers, or topo. */
function CardCover({ group }: { group: GroupListItem }) {
  const images = group.imageUrl ? [group.imageUrl] : group.coverImages.slice(0, 3);
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0 }}>
      {images.length === 0 ? (
        <div className="atlas-topo" style={{ position: "absolute", inset: 0 }} />
      ) : (
        <div style={{ position: "absolute", inset: 0, display: "grid", gridTemplateColumns: `repeat(${images.length}, minmax(0, 1fr))`, gap: 2, background: "rgba(9,12,16,.9)" }}>
          {images.map((src) => (
            <img key={src} src={src} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ))}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            images.length === 0
              ? "linear-gradient(180deg, rgba(9,12,16,.35), rgba(9,12,16,.8))"
              : "linear-gradient(180deg, rgba(9,12,16,.1) 30%, rgba(9,12,16,.85) 100%)",
        }}
      />
    </div>
  );
}

function boardLine(group: GroupListItem, myId: string | undefined): string {
  if (!group.leader) return "Nobody on the board yet this month";
  const leaderIsMe = group.leader.user.userId === myId;
  if (leaderIsMe) return `This month: you lead with ${fmtNumber(group.leader.value)}`;
  const lead = `This month: ${firstName(group.leader.user)} leads with ${fmtNumber(group.leader.value)}`;
  return group.myRank != null
    ? `${lead} · you're ${ordinal(group.myRank)} on ${fmtNumber(group.myValue)}`
    : `${lead} · you're not on it yet`;
}

// Few groups get big cards that share the row; from four on, a regular grid.
function gridFor(count: number): { style: React.CSSProperties; coverHeight: number; className?: string } {
  const base: React.CSSProperties = { display: "grid", gap: 16, minWidth: 0 };
  if (count === 1) {
    return { style: { ...base, gridTemplateColumns: "minmax(0, 1fr)", maxWidth: 880 }, coverHeight: 280 };
  }
  if (count <= 3) {
    return {
      style: { ...base, gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` },
      coverHeight: count === 2 ? 260 : 210,
      className: "r-cols-1",
    };
  }
  return {
    style: { ...base, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))" },
    coverHeight: 150,
  };
}

function GroupCard({ group, coverHeight = 150 }: { group: GroupListItem; coverHeight?: number }) {
  const { user } = useAuth();
  const challenge = group.activeChallenge;
  const left = challenge ? daysBetween(londonToday(), challenge.endsOn) : 0;
  return (
    <Link
      to="/groups/$id"
      params={{ id: group.id }}
      className="grp-lift"
      style={{ ...CARD_STYLE, display: "flex", flexDirection: "column", overflow: "hidden", textDecoration: "none", color: "inherit" }}
    >
      <div style={{ position: "relative", height: coverHeight, flexShrink: 0, display: "flex", alignItems: "flex-end", padding: "14px 16px" }}>
        <CardCover group={group} />
        {group.role !== "member" && (
          <span
            style={{
              ...HERO_CONTROL_STYLE,
              position: "absolute",
              top: 10,
              right: 10,
              height: 22,
              padding: "0 8px",
              fontSize: 11,
              cursor: "inherit",
            }}
          >
            {group.role === "owner" ? "Owner" : "Admin"}
          </span>
        )}
        <span
          style={{
            position: "relative",
            font: "var(--type-h3)",
            fontSize: 21,
            letterSpacing: "-.01em",
            color: HERO_TEXT,
            textShadow: "0 1px 8px rgba(0,0,0,.4)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {group.name}
        </span>
      </div>
      <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0, flex: 1 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {group.members.slice(0, 4).map((m, i) => (
              <span key={m.userId} style={{ marginLeft: i === 0 ? 0 : -7, display: "inline-flex", position: "relative", zIndex: 4 - i }}>
                <MiniAvatar member={m} size={26} />
              </span>
            ))}
          </span>
          <span className="tnum" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
            {fmtNumber(group.memberCount)} member{group.memberCount === 1 ? "" : "s"}
          </span>
        </span>
        <span className="tnum" style={{ fontSize: 13, color: group.leader ? "var(--text-secondary)" : "var(--text-muted)", lineHeight: 1.45 }}>
          {boardLine(group, user?.id)}
        </span>
        {challenge && (
          <span
            style={{
              marginTop: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 10px 4px 4px",
              borderRadius: "var(--radius-pill)",
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-subtle)",
              fontSize: 12.5,
              color: "var(--text-secondary)",
              minWidth: 0,
              alignSelf: "flex-start",
              maxWidth: "100%",
            }}
          >
            <span
              aria-hidden="true"
              className={challenge.imageUrl ? undefined : "atlas-topo"}
              style={{ width: 22, height: 22, borderRadius: "50%", overflow: "hidden", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--status-info)" }}
            >
              {challenge.imageUrl ? (
                <img src={challenge.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <Flag size={11} />
              )}
            </span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{challenge.title}</span>
            <span className="tnum" style={{ color: "var(--text-muted)", flexShrink: 0 }}>
              · {left <= 0 ? "last day" : `${left} day${left === 1 ? "" : "s"} left`}
            </span>
          </span>
        )}
      </div>
    </Link>
  );
}

// ─── Join with a code ─────────────────────────────────────────────────────────

function JoinForm({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [value, setValue] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = parseGroupCode(value);
    if (!parsed) {
      setError("That doesn't look like an invite code or link.");
      return;
    }
    setError(null);
    // Always via the preview page, so the consent line is read before joining.
    void navigate({
      to: "/groups/join/$code",
      params: { code: parsed.code },
      search: parsed.ref ? { ref: parsed.ref } : {},
    });
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label htmlFor="group-code" style={LABEL_STYLE}>
        Paste an invite link or type the code
      </label>
      <input
        id="group-code"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="goatlas.co.uk/groups/join/… or K7M2QX9P"
        autoComplete="off"
        spellCheck={false}
        autoFocus
        style={{ ...INPUT_STYLE, fontFamily: "var(--font-mono)", fontSize: 13 }}
      />
      <span style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>
        Joining is free. You'll see the group before you join.
      </span>
      {error && <ErrorLine>{error}</ErrorLine>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", paddingTop: 4 }}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="secondary" leftIcon={<KeyRound size={14} />} disabled={!value.trim()}>
          Open
        </Button>
      </div>
    </form>
  );
}

// ─── Create ───────────────────────────────────────────────────────────────────

function ProCreateNote() {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <Lock size={16} style={{ color: "var(--text-muted)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
        <p style={{ fontSize: 13.5, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
          Starting a group and running challenges needs Pro. Joining is free, so the rest of your
          group can stay on the free plan and still see the board, the challenges and the feed.
        </p>
        <div>
          <Link
            to="/plan"
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: "var(--control-sm)",
              padding: "0 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-strong)",
              color: "var(--text-primary)",
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            See Pro
          </Link>
        </div>
      </div>
    </div>
  );
}

function CreateGroupForm({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      createGroup({ name: name.trim(), description: description.trim() || null, imageUrl }),
    onSuccess: async ({ groupId }) => {
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      void navigate({ to: "/groups/$id", params: { id: groupId } });
    },
  });

  if (mutation.isError && isUpgradeRequired(mutation.error)) return <ProCreateNote />;

  const trimmed = name.trim();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed && !mutation.isPending) mutation.mutate();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label htmlFor="group-name" style={LABEL_STYLE}>Name</label>
        <input
          id="group-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          placeholder="Tuesday hill club"
          style={INPUT_STYLE}
          autoFocus
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label htmlFor="group-description" style={LABEL_STYLE}>Description (optional)</label>
        <textarea
          id="group-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={280}
          rows={2}
          style={{ ...INPUT_STYLE, height: "auto", padding: 10, resize: "vertical" }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={LABEL_STYLE}>Image (optional)</span>
        <GroupImagePicker name={name} value={imageUrl} onChange={setImageUrl} onBusy={setUploading} />
      </div>
      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
        Groups are private. Nobody finds yours unless a member shares the link.
      </p>
      {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "Couldn't create the group. Try again.")}</ErrorLine>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", paddingTop: 4 }}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!trimmed || uploading || mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create group"}
        </Button>
      </div>
    </form>
  );
}

function CreateGroupDialog({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { isPro, isLoading } = useIsPro();
  const canCreate = isPro || !!user?.isAdmin;
  return (
    <Dialog title={isLoading || canCreate ? "Create a group" : "Groups need Atlas Pro to start"} onClose={onClose} maxWidth={520}>
      {isLoading ? <Skel height="160px" /> : canCreate ? <CreateGroupForm onCancel={onClose} /> : <ProCreateNote />}
    </Dialog>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

const STEPS = [
  { title: "Start a group", body: "Name it and share it. Starting one needs Pro." },
  { title: "Share the link", body: "Anyone with it can join, free plan included." },
  { title: "Compare what you collect", body: "A monthly board, collection boards and challenges with an end date." },
];

const SAMPLE_ROWS = [
  { name: "Hannah", value: 14 },
  { name: "Owen", value: 11 },
  { name: "You", value: 9, me: true },
  { name: "Ruth", value: 4 },
];

function EmptyExplainer({ actions }: { actions: React.ReactNode }) {
  return (
    <div
      className="r-cols-1"
      style={{
        ...CARD_STYLE,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "clamp(20px, 3vw, 32px)", display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
        <div>
          <h2 style={{ font: "var(--type-h3)", fontSize: 20, color: "var(--text-primary)", margin: 0 }}>You're not in a group yet</h2>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "6px 0 0", lineHeight: 1.5, maxWidth: 460 }}>
            A group is a private board for the people you walk with. Here is how one works.
          </p>
        </div>
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {STEPS.map((step, i) => (
            <li key={step.title} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span
                className="tnum"
                style={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  borderRadius: "50%",
                  border: "1px solid var(--border-default)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                }}
              >
                {i + 1}
              </span>
              <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{step.title}</span>
                <span style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.45 }}>{step.body}</span>
              </span>
            </li>
          ))}
        </ol>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
      </div>
      <div
        aria-hidden="true"
        className="atlas-topo"
        style={{ position: "relative", padding: "clamp(20px, 3vw, 32px)", display: "flex", alignItems: "center", justifyContent: "center", borderLeft: "1px solid var(--border-subtle)", minWidth: 0 }}
      >
        <div style={{ ...CARD_STYLE, width: "100%", maxWidth: 320, padding: 14, opacity: 0.7, display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="eyebrow" style={{ fontSize: 9.5, marginBottom: 6 }}>Example · collected this month</span>
          {SAMPLE_ROWS.map((row, i) => (
            <span
              key={row.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 8px",
                borderRadius: "var(--radius-md)",
                background: row.me ? "var(--accent-soft)" : "transparent",
                border: `1px solid ${row.me ? "var(--border-gold)" : "transparent"}`,
              }}
            >
              <span className="tnum" style={{ width: 14, fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>{i + 1}</span>
              <MiniAvatar member={{ name: row.name, username: null, avatarUrl: null }} size={24} />
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>{row.name}</span>
              <span className="tnum" style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{row.value}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function GroupsPage() {
  usePageMeta({
    title: "Groups - Atlas",
    description: "Your private Atlas groups.",
    canonicalPath: "/groups",
    noindex: true,
  });

  const groupsQuery = useQuery({ queryKey: ["groups"], queryFn: fetchGroups });
  const groups = groupsQuery.data?.items ?? [];
  // Phones stack the cards, so tall covers would just push everything down.
  const phone = useMediaQuery("(max-width: 760px)");
  const [dialog, setDialog] = React.useState<"join" | "create" | null>(null);

  const actions = (
    <>
      <Button variant="secondary" leftIcon={<KeyRound size={14} />} onClick={() => setDialog("join")}>
        Join with a code
      </Button>
      <Button leftIcon={<Plus size={14} />} onClick={() => setDialog("create")}>
        Create a group
      </Button>
    </>
  );
  const empty = groupsQuery.isSuccess && groups.length === 0;

  return (
    <GroupsPageFrame wide>
      <style>{LIFT_CSS}</style>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: "1 1 320px" }}>
          <p className="eyebrow" style={{ fontSize: 11, margin: "0 0 4px" }}>Community</p>
          <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>Groups</h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "6px 0 0", maxWidth: 560, lineHeight: 1.5 }}>
            Private boards and challenges for the people you walk with. Only members see what happens here.
          </p>
        </div>
        {!empty && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
      </div>

      {groupsQuery.isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 16 }}>
          <Skel height="270px" radius="var(--radius-lg)" />
          <Skel height="270px" radius="var(--radius-lg)" />
          <Skel height="270px" radius="var(--radius-lg)" />
        </div>
      ) : groupsQuery.isError ? (
        <EmptyState
          icon={<UsersRound size={20} />}
          title="Couldn't load your groups"
          hint={groupErrorMessage(groupsQuery.error, "Try again in a moment.")}
          action={{ label: "Try again", onClick: () => void groupsQuery.refetch() }}
        />
      ) : empty ? (
        <EmptyExplainer actions={actions} />
      ) : (
        (() => {
          const grid = gridFor(groups.length);
          return (
            <div className={grid.className} style={grid.style}>
              {groups.map((g) => (
                <GroupCard key={g.id} group={g} coverHeight={phone ? Math.min(grid.coverHeight, 170) : grid.coverHeight} />
              ))}
            </div>
          );
        })()
      )}

      {dialog === "join" && (
        <Dialog title="Join with a code" onClose={() => setDialog(null)} maxWidth={460}>
          <JoinForm onCancel={() => setDialog(null)} />
        </Dialog>
      )}
      {dialog === "create" && <CreateGroupDialog onClose={() => setDialog(null)} />}
    </GroupsPageFrame>
  );
}
